import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { ShieldCheck, ShieldAlert, ShieldX } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATES = {
  valid: { icon: ShieldCheck, color: "text-green-600", title: "Genuine and valid", text: "This pro forma was issued by Compliance Management Systems and has not been altered." },
  expired: { icon: ShieldAlert, color: "text-amber-600", title: "Genuine but expired", text: "This pro forma was issued by us but is past its validity date. Please request a new one." },
  paid: { icon: ShieldCheck, color: "text-blue-600", title: "Genuine — paid", text: "This pro forma was issued by Compliance Management Systems and has been paid in full." },
  revoked: { icon: ShieldX, color: "text-red-600", title: "Revoked", text: "This pro forma has been withdrawn and must not be used for payment." },
  code_mismatch: { icon: ShieldX, color: "text-red-600", title: "Security code does not match", text: "The code entered does not match our records. The document may have been altered." },
  not_found: { icon: ShieldX, color: "text-red-600", title: "Not found", text: "We have no issued pro forma with this number." },
};

export default function VerifyProforma({ kind = "proforma" }) {
  const { number } = useParams();
  const isReceipt = kind === "receipt";
  const docName = isReceipt ? "receipt" : "pro forma";
  const [params] = useSearchParams();
  const [code, setCode] = useState(params.get("code") || "");
  const [result, setResult] = useState(null);

  const check = (c) => axios.get(`${API}/${isReceipt ? "receipts" : "proforma"}/verify/${encodeURIComponent(number)}`, { params: { code: c } }).then((r) => setResult(r.data));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (code) check(code); }, [number]);

  const base = result && (STATES[result.status] || STATES.not_found);
  const s = base && (isReceipt && result.status === "valid"
    ? { ...base, title: "Genuine receipt", text: "This receipt was issued by Compliance Management Systems for a confirmed payment and has not been altered." }
    : isReceipt && result.status === "revoked"
      ? { ...base, title: "No longer valid", text: "The payment linked to this receipt has been reversed or cancelled." }
      : { ...base, text: base.text.replace(/pro forma/g, docName) });
  return (
    <div className="min-h-screen bg-slate-50 py-16">
      <title>{`Verify ${isReceipt ? "Receipt" : "Pro Forma"} ${number} | Compliance Management Systems`}</title>
      <meta name="robots" content="noindex" />
      <div className="container mx-auto px-4 max-w-xl">
        <h1 className="font-heading font-bold text-3xl text-primary mb-2">Verify a {isReceipt ? "Payment Receipt" : "Pro Forma Invoice"}</h1>
        <p className="text-muted-foreground mb-6">{isReceipt ? "Receipt" : "Pro forma"} <strong>{number}</strong></p>
        <div className="flex gap-2 mb-6">
          <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Security code, e.g. 8B66-E028-A278" data-testid="verify-code-input" />
          <Button onClick={() => check(code)} className="bg-brand text-white" data-testid="verify-btn">Verify</Button>
        </div>
        {s && (
          <Card data-testid="verify-result">
            <CardContent className="p-6">
              <div className={`flex items-center gap-3 ${s.color}`}>
                <s.icon className="h-8 w-8" />
                <p className="font-heading font-bold text-xl" data-testid="verify-status">{s.title}</p>
              </div>
              <p className="text-sm text-muted-foreground mt-2">{s.text}</p>
              {result.company_name && (
                <dl className="grid grid-cols-2 gap-3 text-sm mt-5 border-t pt-4">
                  <dt className="text-muted-foreground">Issued to</dt><dd className="font-medium">{result.company_name}</dd>
                  <dt className="text-muted-foreground">Course</dt><dd className="font-medium">{result.participants} × {result.course_title}</dd>
                  <dt className="text-muted-foreground">{isReceipt ? "Order total" : "Total"}</dt><dd className="font-medium" data-testid="verify-total">{`USD ${Number(result.total).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}</dd>
                  {isReceipt && result.amount_paid != null && <><dt className="text-muted-foreground">Amount paid</dt><dd className="font-medium" data-testid="verify-amount-paid">{`USD ${Number(result.amount_paid).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}</dd></>}
                  {isReceipt && result.balance > 0 && <><dt className="text-muted-foreground">Balance outstanding</dt><dd className="font-medium text-amber-700" data-testid="verify-balance">{`USD ${Number(result.balance).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}</dd></>}
                  <dt className="text-muted-foreground">{isReceipt ? "Paid on" : "Issued"}</dt><dd className="font-medium">{result.issued_date}</dd>
                  {result.valid_until && <><dt className="text-muted-foreground">Valid until</dt><dd className="font-medium">{result.valid_until}</dd></>}
                </dl>
              )}
            </CardContent>
          </Card>
        )}
        <p className="text-xs text-muted-foreground mt-6">Questions? <Link to="/contact" className="text-brand underline">Contact us</Link>.</p>
      </div>
    </div>
  );
}
